import type { BodyJson } from 'contracts/body';
import { Box3, LineSegments, Matrix4, Mesh, MeshPhysicalMaterial } from 'three';
import type { Color, Vector3, BufferGeometry, EdgesGeometry } from 'three';

import { SimpleProgressController } from '@frontend/controllers';
import type { ProgressSignal } from '@frontend/controllers';
import {
  addEventListenerAndCollectRemovers,
  runRemoveEventListeners,
} from '@frontend/utils';

// eslint-disable-next-line no-restricted-imports
import {
  getOrCreateThreeColor,
  getOrCreateThreeColorArray,
  type ColorRGBArray,
} from '../../colors';
// eslint-disable-next-line no-restricted-imports
import { MATERIAL_PROPERTIES, PART_TYPES, ENTITY_TYPES } from '../../constants';
// eslint-disable-next-line no-restricted-imports
import { disposeThreeJS } from '../../geom-utils';
// eslint-disable-next-line no-restricted-imports
import { getLineMaterial } from '../../materials';
// eslint-disable-next-line no-restricted-imports
import type {
  AsyncLoadable,
  SyncLoadable,
} from '../../renderer/loadingPriorityQueue';
// eslint-disable-next-line no-restricted-imports
import type { HasRenderableWithGeometry } from '../../renderer/renderer';
import type Body from '../body';
import type Part from '../part';
import { constructEntityName } from '../step/utils';

// import type { ActiveMetaData } from '../step/utils';
const wireColor = getOrCreateThreeColor(0x000000);

export class STLBody
  implements
    AsyncLoadable,
    SyncLoadable,
    Omit<
      HasRenderableWithGeometry,
      'geometry' | 'metaData' | 'activeMetaData' | 'translate'
    >
{
  public static createProgress(
    id: string,
    part: Part<PART_TYPES.STL>,
  ): SimpleProgressController | undefined {
    return new SimpleProgressController({
      id,
      description: `Loading Body Geometry for ${
        part.name || part.id
      } (instance ${id})`,
      todoCount: 1,
      expectedMs: 1,
      start: false,
    });
  }

  public readonly id?: string;
  public readonly type: PART_TYPES.STL = PART_TYPES.STL;
  public readonly name?: string;
  public readonly partUuid?: string; //actually partFileUuid
  public readonly transform: Matrix4;
  public readonly baseColor: Color;
  public readonly baseRGBColor: ColorRGBArray;
  public readonly baseOpacity: number;
  // public readonly metaData = {};
  // public readonly activeMetaData: ActiveMetaData = {};
  public readonly material: MeshPhysicalMaterial;

  public wireframes: LineSegments[] = [];
  public meshes: Mesh<BufferGeometry, MeshPhysicalMaterial>[] = [];
  private loaded?: Promise<number>; //presence indicates this.isLoading. Resolves when loaded
  private _boundingBox?: Box3; //axis aligned bounding box
  private disposeFns: (() => void)[] = [];
  private isDisposed?: boolean;

  constructor(
    data: BodyJson,
    private readonly part: Part<PART_TYPES.STL>,
    private readonly host: Body<PART_TYPES.STL>,
    private _progress?: SimpleProgressController, //create progress in advance so it can be composed and composedProgress closed. Use STLBody.createProgress()
    private readonly signal?: AbortSignal, //AbortSignal to abort loading
  ) {
    this.id = data.id;
    this.name = data.name;
    this.partUuid = data.partFileUuid || data.part_uuid; //pcad provides part_uuid
    this.transform = new Matrix4();
    this.transform.fromArray(data.transform || new Matrix4().toArray());

    this.baseColor = getOrCreateThreeColor(data.color);
    this.baseRGBColor = getOrCreateThreeColorArray(data.color);
    this.baseOpacity = data.opacity
      ? data.opacity
      : MATERIAL_PROPERTIES.OPACITY;

    this.material = new MeshPhysicalMaterial({
      transparent: true,
      opacity: this.baseOpacity,
      reflectivity: 0.1,
      metalness: 0.1,
      //Note: The material vertexColors MUST be defined for color buffer attribute in this.part.geometry.geometry
      //(see paint-utils.ts/paintExplicitStlFaces() which defines color buffer attribute) to work with
      //this.mesh. (Can set to truthy value such as THREE.FaceColors which === 1 like many three.js examples.
      //But using, true because FaceColors is not declared in typescript definition)
      vertexColors: true,
    });
    this.material.color.copy(this.baseColor);
    if (signal) {
      addEventListenerAndCollectRemovers(
        this.disposeFns,
        signal,
        'abort',
        () => {
          this.dispose();
        },
      );
    }
  }

  public static factory(
    data: BodyJson,
    part: Part<PART_TYPES.STL>,
    host: Body<PART_TYPES.STL>,
    signal?: AbortSignal, //AbortSignal to abort loading
  ): [STLBody, ProgressSignal<SimpleProgressController>?] {
    const progress = STLBody.createProgress(`${data.id}-STLBody`, part);
    return [new STLBody(data, part, host, progress, signal), progress?.signal];
  }
  public dispose() {
    if (this.isDisposed) return;
    this.isDisposed = true;
    runRemoveEventListeners(this.disposeFns);
    this.abortLoad();
    disposeThreeJS(this.meshes, this.wireframes);
  }

  public get renderable() {
    return this.host.group;
  }

  public get boundingBox(): Box3 {
    if (!this.isLoaded) {
      throw new Error('STL geometry is not loaded yet');
    }
    return this._boundingBox as Box3;
  }

  public get faceVerticesCount(): number {
    return this.part.geometry.faceVerticesCount;
  }

  public get edgeVerticesCount(): number {
    return this.part.geometry.edgeVerticesCount;
  }

  public get geometryVerticesCount(): number {
    return this.part.geometry.geometryVerticesCount;
  }

  public get progress(): ProgressSignal<SimpleProgressController> | undefined {
    return this._progress?.signal;
  }

  public get isLoaded(): boolean {
    return this.meshes.length > 0 || this.wireframes.length > 0;
  }

  public get isLoading(): boolean {
    return !!this.loaded;
  }

  public pause(): void {
    if (!this.signal?.aborted && this.part.isLoading) {
      this.part.pause();
    }
  }

  public get isPaused(): boolean {
    return this.part.isPaused;
  }

  public resume(): void {
    if (!this.signal?.aborted && this.part.isLoading) {
      this.part.resume();
    }
  }

  public abortLoad() {
    if (this.loaded) {
      this.pause(); //force pause so we can reject
      this.loaded.catch(() => Promise.resolve()); //the promise's rejection should be handled at least once
      delete this.loaded;
    }
    this.part.abortLoad();
    if (this._progress) {
      this._progress.done();
    }
  }

  public load(): void {
    if (this.isLoaded || this.signal?.aborted) {
      return;
    }
    if (this.isLoading) {
      throw new Error(
        'Cannot load body synchronously that already started async loading',
      );
    }
    if (!this.part.isLoaded) {
      //Should be loaded already, but if not, load it
      this.part.load();
    }
    this._load();
  }

  //Note: loadAsync() for STLBody is effectively same as sync this.load()
  //It is created with same interface as STEPBody's loadAsync
  public async loadAsync(
    lastPause: number = performance.now(),
  ): Promise<number> {
    if (this.isLoaded || this.signal?.aborted) {
      return Promise.resolve(lastPause);
    } else if (!this.loaded) {
      this.loaded = (async (): Promise<number> => {
        if (!this.part.isLoaded) {
          //eslint-disable-next-line no-param-reassign
          lastPause = await this.part.loadAsync(lastPause);
        }
        this._load();
        delete this.loaded;
        return lastPause;
      })();
    } else if (this.isPaused) {
      this.resume();
    }
    return this.loaded;
  }

  public translateFullDetailRepBox(v: Vector3) {
    this._boundingBox?.translate(v);
  }

  public get geometries(): BufferGeometry[] {
    return this.part.geometry.geometries;
  }

  public updateTodoCount() {
    //noop for STL (Added for parity with STEPBody)
  }

  private _load() {
    this._progress?.start();
    const { geometries, wireGeometries } = this.part.geometry;
    for (const [index, g] of geometries.entries()) {
      const mesh = new Mesh(g, this.material);
      mesh.name = constructEntityName(
        this.id ?? 'undefined',
        ENTITY_TYPES.STL,
        index,
      );
      this.meshes.push(mesh);

      this.host.fullDetailRep.add(mesh);
      const mat = getLineMaterial(wireColor, 2);
      const wireframe = new LineSegments(
        wireGeometries[index] as EdgesGeometry,
        mat,
      );
      wireframe.visible = false;
      this.wireframes.push(wireframe);
      this.host.fullDetailRep.add(wireframe);
    }
    this._boundingBox = new Box3().setFromObject(this.host.fullDetailRep);
    if (this._progress) {
      this._progress.done();
      delete this._progress;
    }
    this.resume(); //cleanup resumeController if it exists
  }
}
